import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportDashboard } from '@/lib/api/reports.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportFilters, useReportQueryParams } from '@/pages/reports/report-filters-context.js';

export function ReportsDashboardPage() {
  const f = useReportFilters();
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-dashboard', qBuilt],
    queryFn: () => fetchReportDashboard(qBuilt),
  });

  const d = q.data as
    | {
        todayRevenue?: string;
        yesterdayRevenue?: string;
        weekRevenue?: string;
        monthRevenue?: string;
        openTickets?: number;
        todayTicketsClosed?: number;
        avgTicketValue?: string;
        avgRepairTime?: string;
        revenueLast30Days?: Array<{ date: string; revenue: string; tickets: number }>;
        ticketsByStatus?: Record<string, number>;
        topRepairsToday?: Array<{ serviceName: string; count: number; revenue: string }>;
        cashDrawerStatus?: { isOpen: boolean; expectedCash: string | null; openedAt: string | null };
        recentTickets?: Array<{ id: string; ticketNumber: string; customerName: string; status: string; createdAt: string }>;
        lowStockCount?: number;
        period?: { start: string; end: string };
      }
    | undefined;

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading reports…</div>;
  if (q.isError || !d) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load dashboard.</div>;

  const prior = Number(d.yesterdayRevenue ?? 0);
  const periodRev = Number(d.todayRevenue ?? 0);
  const revDelta = prior > 0 ? ((periodRev - prior) / prior) * 100 : 0;

  const statusChart = Object.entries(d.ticketsByStatus ?? {}).map(([name, value]) => ({ name: name.replace(/_/g, ' '), value }));

  const revSeries = (d.revenueLast30Days ?? []).map((r) => ({
    date: r.date.length > 10 ? r.date.slice(0, 10) : r.date.slice(5),
    revenue: Number(r.revenue),
    tickets: r.tickets,
  }));

  const rangeLabel =
    f.dateFrom && f.dateTo
      ? f.dateFrom === f.dateTo
        ? f.dateFrom
        : `${f.dateFrom} → ${f.dateTo}`
      : f.period;

  return (
    <div className="space-y-8 print:space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Period revenue</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{formatCurrency(d.todayRevenue)}</p>
          <p className="mt-1 text-xs text-slate-500">
            {revDelta >= 0 ? '↑' : '↓'} {Math.abs(revDelta).toFixed(0)}% vs prior period
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">{rangeLabel}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Trailing week revenue</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{formatCurrency(d.weekRevenue)}</p>
          <p className="mt-1 text-xs text-slate-500">7 days ending selected To date</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Month-to-date revenue</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{formatCurrency(d.monthRevenue)}</p>
          <p className="mt-1 text-xs text-slate-500">Calendar month of selected To date</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Open tickets</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{d.openTickets ?? 0}</p>
          <p className="mt-1 text-xs text-slate-500">Live pipeline (not date-filtered)</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Closed in period</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{d.todayTicketsClosed ?? 0}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Avg ticket (period)</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{formatCurrency(d.avgTicketValue)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Avg repair time</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{d.avgRepairTime ?? '—'} min</p>
          <p className="mt-1 text-xs text-slate-500">Logged timer entries in period</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Low stock SKUs</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{d.lowStockCount ?? 0}</p>
          <Link to="/inventory" className="mt-1 inline-block text-xs font-semibold text-primary hover:underline">
            Inventory
          </Link>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Revenue ({rangeLabel})</h3>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Tickets by status (pipeline)</h3>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusChart} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Top repairs in period</h3>
          <ol className="mt-3 space-y-2 text-sm">
            {(d.topRepairsToday ?? []).map((r, i) => (
              <li key={r.serviceName + i} className="flex justify-between gap-4">
                <span>
                  {i + 1}. {r.serviceName}{' '}
                  <span className="text-slate-500">
                    × {r.count}
                  </span>
                </span>
                <span className="font-semibold tabular-nums">{formatCurrency(r.revenue)}</span>
              </li>
            ))}
            {!(d.topRepairsToday ?? []).length ? <li className="text-slate-500">No repairs in this period.</li> : null}
          </ol>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Cash drawer</h3>
          {d.cashDrawerStatus?.isOpen ? (
            <p className="mt-3 text-sm text-emerald-800">
              🟢 Session open — {d.cashDrawerStatus.expectedCash != null ? formatCurrency(d.cashDrawerStatus.expectedCash) : '—'} expected
            </p>
          ) : (
            <p className="mt-3 text-sm text-red-800">🔴 No open session for this store.</p>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-950">Recent tickets</h3>
          <Link to="/tickets" className="text-sm font-semibold text-primary hover:underline">
            Open queue
          </Link>
        </div>
        <ul className="mt-3 divide-y divide-slate-100 text-sm">
          {(d.recentTickets ?? []).map((t) => (
            <li key={t.id} className="flex justify-between py-2">
              <Link to={`/tickets/${t.id}`} className="font-medium text-primary hover:underline">
                {t.ticketNumber}
              </Link>
              <span className="text-slate-600">{t.customerName}</span>
              <span className="text-xs uppercase text-slate-500">{t.status.replace(/_/g, ' ')}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
